import type { KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { Check } from 'lucide-react'
import type { ProcessStepVM } from '@/utils/workflowOverview'
import { cn } from '@/utils'
import { LANDING_RING_ON_CARD } from '@/components/records/landingRing'
import { executorTone, hueClasses } from '../executorTone'
import { accessibleName, dueLine, levelMark } from './mapText'
import type { CardBox } from './mapTypes'

export interface MapCardProps {
  step: ProcessStepVM
  /** Absolute in the map when given; static in the list when not. */
  box?: CardBox
  current: boolean
  visited: boolean
  /** SA-1037: an exit jumped over this step on the record (`pathSkips`). */
  skipped?: boolean
  /** SA-1328: the card a landing names, ringed like a record section's. */
  ring?: boolean
  tabIndex: 0 | -1
  /** D11: a link when given, else a button that reports `onSelect`. */
  href?: string
  onSelect?: (id: string) => void
  onKeyDown: (e: KeyboardEvent<HTMLElement>, id: string) => void
  onFocus: (id: string) => void
}

/**
 * One card (SA-448 spec §4.4; dressed to the wireframe by SA-460, spec
 * §4.1): a wrapper at the layout's box holding ONE interactive element —
 * the stripe, the number and title, the dots and seat, the due line and the
 * level inside it; the CURRENT tab as its sibling, so no interactive element
 * nests another. Colour comes from `executorTone` only (D2).
 */
export function MapCard({ step, box, current, visited, skipped = false, ring = false, tabIndex, href, onSelect, onKeyDown, onFocus }: MapCardProps) {
  const passed = skipped && !current && !visited
  const name = accessibleName(step, current, visited, passed)
  const tone = executorTone(step)
  const level = levelMark(step)
  const done = visited && !current
  const inner = (
    <>
      <span aria-hidden="true" data-testid="map-card-stripe" className="absolute inset-y-0 left-0 w-[5px] rounded-l-lg" style={tone.stripeStyle} />
      <span className="flex items-baseline gap-1.5 text-[12.5px] font-semibold leading-[15px] text-foreground">
        <span className="shrink-0 font-mono text-[10px] font-medium text-muted-foreground">{step.number}</span>
        <span className="line-clamp-2">{step.title}</span>
      </span>
      <span className="mt-1 flex items-center gap-1.5 text-[11px] leading-[13px] text-foreground/80">
        <span className="flex shrink-0 items-center gap-[3px]">
          {tone.stripe.map((hue) => (
            <i key={hue} data-testid="map-card-dot" className={cn('inline-block size-[7px] rounded-full', hueClasses(hue).dot)} />
          ))}
        </span>
        <span className="truncate">{step.who}</span>
      </span>
      <span className="truncate text-[10.5px] leading-[13px] text-muted-foreground">{dueLine(step)}</span>
      <span className="mt-auto flex items-baseline justify-between text-[9.5px] font-semibold uppercase leading-[12px] tracking-[.06em] text-muted-foreground">
        <span>{level ?? ''}</span>
        {done && (
          <span className="inline-flex items-center gap-0.5 text-[10.5px] normal-case tracking-normal text-success">
            <Check aria-hidden="true" className="size-3" />
            done
          </span>
        )}
        {passed && (
          <span className="text-[10.5px] normal-case tracking-normal text-muted-foreground">skipped</span>
        )}
      </span>
    </>
  )
  const cls = cn(
    'relative flex h-full w-full flex-col overflow-hidden rounded-lg bg-card py-1.5 pl-3.5 pr-2.5 text-left shadow-xs outline-none',
    'hover:border-primary/40 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
    // SA-1077: the current step is lifted (a primary border and a tinted fill); a done step steps back.
    current ? 'border-2 border-primary bg-map-card-current' : 'border border-border',
    done && 'opacity-75',
    passed && 'border-dashed opacity-60 focus-visible:opacity-100',
    ring && LANDING_RING_ON_CARD,
  )
  const common = { className: cls, 'aria-label': name, tabIndex, onKeyDown: (e: KeyboardEvent<HTMLElement>) => onKeyDown(e, step.id), onFocus: () => onFocus(step.id) }
  return (
    <div
      data-step-id={step.id}
      data-current={current ? 'true' : 'false'}
      data-visited={visited ? 'true' : 'false'}
      data-skipped={passed ? 'true' : 'false'}
      data-landing={ring ? 'true' : undefined}
      className={box ? 'absolute' : 'relative'}
      style={box ? { left: box.x, top: box.y, width: box.w, height: box.h } : undefined}
    >
      {href ? (
        <Link to={href} draggable={false} {...common}>{inner}</Link>
      ) : (
        <button type="button" onClick={onSelect ? () => onSelect(step.id) : undefined} {...common}>{inner}</button>
      )}
      {current && (
        <span aria-hidden="true" className="pointer-events-none absolute -top-2 right-2 rounded-full bg-primary px-2 py-px text-[9px] font-semibold uppercase tracking-wide text-primary-foreground">
          Now
        </span>
      )}
    </div>
  )
}
